import { SceneFilter } from "@/types/scene-filter";

/**
 * This type encapsulates configuration flags to enable or disable the
 * individual steps of the rendering pipeline.
 */
export type FlyWalkRenderingState = {
  floorPlanPassEnabled: boolean;
  previousImagePassEnabled: boolean;
  panoPassEnabled: boolean;
  pointCloudPassEnabled: boolean;
  cadModelPassEnabled: boolean;
  gaussianSplatsPassEnabled: boolean;
};

/**
 *
 * @param state An object with enablement flags for the rendering pipeline
 * @param idx An index between zero and six
 * @returns The flag stored in the given object at the given index.
 */
export function renderingFlagByIdx(
  state: FlyWalkRenderingState,
  idx: number,
): boolean {
  switch (idx) {
    case 0:
      return state.floorPlanPassEnabled;
    case 1:
      return state.previousImagePassEnabled;
    case 2:
      return state.panoPassEnabled;
    case 3:
      return state.pointCloudPassEnabled;
    case 4:
      return state.cadModelPassEnabled;
    case 5:
      return state.gaussianSplatsPassEnabled;
    default:
      throw new Error(`Invalid render dispatch index ${idx}`);
  }
}

/**
 *
 * @param a Left-hand argument
 * @param b Right-hand argument
 * @returns The logical OR between the input arguments
 */
export function unionRenderingStates(
  a: FlyWalkRenderingState,
  b: FlyWalkRenderingState,
): FlyWalkRenderingState {
  return {
    floorPlanPassEnabled: a.floorPlanPassEnabled || b.floorPlanPassEnabled,
    previousImagePassEnabled:
      a.previousImagePassEnabled || b.previousImagePassEnabled,
    panoPassEnabled: a.panoPassEnabled || b.panoPassEnabled,
    pointCloudPassEnabled: a.pointCloudPassEnabled || b.pointCloudPassEnabled,
    cadModelPassEnabled: a.cadModelPassEnabled || b.cadModelPassEnabled,
    gaussianSplatsPassEnabled:
      a.gaussianSplatsPassEnabled || b.gaussianSplatsPassEnabled,
  };
}

/**
 *
 * @param sceneFilter a value for the scene filter input
 * @returns The corresponding pipeline passes that should be enabled
 */
export function sceneFilter2RenderingState(
  sceneFilter: SceneFilter | null,
): FlyWalkRenderingState {
  return {
    floorPlanPassEnabled: sceneFilter !== SceneFilter.Pano,
    previousImagePassEnabled: false,
    panoPassEnabled: sceneFilter === SceneFilter.Pano,
    pointCloudPassEnabled:
      sceneFilter === SceneFilter.PointCloud ||
      sceneFilter === SceneFilter.Overlay,
    cadModelPassEnabled:
      sceneFilter === SceneFilter.Cad || sceneFilter === SceneFilter.Overlay,
    gaussianSplatsPassEnabled: sceneFilter === SceneFilter.GaussianSplats,
  };
}
